import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Floating-label, underline-only text field (Material "standard" variant).
 *
 * The label sits inside the field as a placeholder when empty/unfocused, then
 * floats up above the line on focus or when filled. Only a bottom border — no
 * full box — for a lighter, more modern auth form (86d3cff1t). Tokens only, so
 * it themes across light / dark / Industrial.
 *
 * Mechanics: the input renders `placeholder=" "` so the `:placeholder-shown`
 * state tracks emptiness; the label's default (floated) state is overridden by
 * `peer-placeholder-shown:*` when empty and re-applied by `peer-focus:*` on
 * focus.
 */
interface FloatingLabelInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  id: string;
  error?: boolean;
  /** Optional control rendered at the trailing edge (e.g. a show-password toggle). */
  trailing?: React.ReactNode;
}

export const FloatingLabelInput = React.forwardRef<
  HTMLInputElement,
  FloatingLabelInputProps
>(({ className, label, id, error, trailing, ...props }, ref) => {
  return (
    <div className="relative">
      <input
        id={id}
        ref={ref}
        placeholder=" "
        className={cn(
          'peer block w-full appearance-none border-0 border-b bg-transparent px-0 pb-2.5 pt-4 text-sm text-foreground transition-colors focus:outline-none focus:ring-0',
          trailing && 'pr-8',
          error
            ? 'border-destructive focus:border-destructive'
            : 'border-input focus:border-primary',
          className,
        )}
        {...props}
      />
      <label
        htmlFor={id}
        className={cn(
          'pointer-events-none absolute left-0 top-4 origin-top-left -translate-y-6 scale-75 text-sm transition-all duration-200',
          'peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100',
          'peer-focus:-translate-y-6 peer-focus:scale-75',
          error
            ? 'text-destructive'
            : 'text-muted-foreground peer-focus:text-primary',
        )}
      >
        {label}
      </label>
      {trailing && (
        <div className="absolute bottom-2 right-0 flex items-center">
          {trailing}
        </div>
      )}
    </div>
  );
});
FloatingLabelInput.displayName = 'FloatingLabelInput';
